// Debug mode: run `window.megaMenuDebug = true` in console to keep panels open
const isDebug = () => (window as any).megaMenuDebug === true;

const initEkMegaMenu = (scope: ParentNode = document) => {
  const megaMenus = Array.from(scope.querySelectorAll("[data-mega-menu]")) as HTMLElement[];

  for (const megaMenu of megaMenus) {
    if (megaMenu.dataset.initialized === "true") continue;
    megaMenu.dataset.initialized = "true";

    const nav = megaMenu.querySelector("[data-mega-menu-nav]") as HTMLElement | null;
    const indicator = megaMenu.querySelector("[data-mega-menu-indicator]") as HTMLElement | null;
    const triggers = Array.from(megaMenu.querySelectorAll("[data-mega-menu-trigger]")) as HTMLElement[];

    const panelsContainer = megaMenu.querySelector(".mega-menu-desktop__panels") as HTMLElement | null;
    const panels = panelsContainer
      ? (Array.from(panelsContainer.querySelectorAll("[data-mega-menu-panel]")) as HTMLElement[])
      : [];
    const backdrop = megaMenu.querySelector("[data-mega-menu-backdrop]") as HTMLElement | null;
    const holder = megaMenu.closest("[data-ek-header-bar]") as HTMLElement | null;

    if (!nav || !indicator || triggers.length === 0 || panels.length === 0) continue;

    const getTriggerHandle = (trigger: HTMLElement | null) => trigger?.dataset.panelHandle || null;

    const moveIndicator = (trigger: HTMLElement | null) => {
      if (!trigger) {
        indicator.style.width = "0px";
        indicator.style.opacity = "0";
        indicator.style.transform = "translateX(0px)";
        return;
      }

      const navRect = nav.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const left = triggerRect.left - navRect.left;

      indicator.style.width = `${triggerRect.width}px`;
      indicator.style.opacity = "1";
      indicator.style.transform = `translateX(${left}px)`;
    };

    let menuOpen = false;
    let bgRemoveTimer: ReturnType<typeof setTimeout> | null = null;

    const openPanel = (handle: string | null) => {
      const wasOpen = menuOpen;
      let anyOpen = false;

      if (wasOpen && handle !== null) {
        for (const panel of panels) {
          panel.style.transition = "none";
        }
      }

      for (const panel of panels) {
        const isOpen = handle !== null && panel.dataset.panelHandle === handle;
        panel.dataset.open = isOpen ? "true" : "false";
        if (isOpen) anyOpen = true;
      }

      if (wasOpen && handle !== null) {
        void panels[0]?.offsetHeight;
        for (const panel of panels) {
          panel.style.transition = "";
        }
      }

      menuOpen = anyOpen;
      if (backdrop) {
        backdrop.style.opacity = anyOpen ? "1" : "0";
      }
      if (holder) {
        if (anyOpen) {
          if (bgRemoveTimer) {
            clearTimeout(bgRemoveTimer);
            bgRemoveTimer = null;
          }
          holder.classList.add("bg-white");
          holder.classList.remove("shadow-xl");
        } else if (window.scrollY <= 0) {
          bgRemoveTimer = setTimeout(() => {
            if (!menuOpen && window.scrollY <= 0) {
              holder.classList.remove("bg-white");
            }
            bgRemoveTimer = null;
          }, 250);
        }
      }
    };

    const activateTrigger = (trigger: HTMLElement | null) => {
      moveIndicator(trigger);
      openPanel(getTriggerHandle(trigger));
    };

    const getCurrentTrigger = () =>
      (megaMenu.querySelector('[data-mega-menu-trigger][data-current="true"][data-panel-handle]') as HTMLElement | null) ||
      null;

    const resetState = () => {
      const current = getCurrentTrigger();
      if (current) {
        moveIndicator(current);
        openPanel(null);
        return;
      }

      moveIndicator(null);
      openPanel(null);
    };

    for (const trigger of triggers) {
      const isSale = trigger.dataset.megaMenuSale !== undefined;

      if (isSale) {
        // Sale: plain link — never open a mega-menu panel or underline indicator
        const onHover = () => {
          openPanel(null);
          moveIndicator(null);
        };
        trigger.addEventListener("mouseenter", onHover);
        trigger.addEventListener("focus", onHover);
      } else if (trigger.dataset.panelHandle) {
        // Has panel: open panel + move indicator
        const onActivate = () => activateTrigger(trigger);
        trigger.addEventListener("mouseenter", onActivate);
        trigger.addEventListener("focus", onActivate);
      } else {
        // Normal link (e.g. Magazin): close panels + move indicator + add bg-white
        const onHover = () => {
          moveIndicator(trigger);
          openPanel(null);
          if (bgRemoveTimer) { clearTimeout(bgRemoveTimer); bgRemoveTimer = null; }
          if (holder) holder.classList.add("bg-white");
        };
        trigger.addEventListener("mouseenter", onHover);
        trigger.addEventListener("focus", onHover);
      }
    }

    megaMenu.addEventListener("mouseleave", () => {
      if (!isDebug()) resetState();
    });

    document.addEventListener(
      "mousedown",
      (event) => {
        if (isDebug() || !menuOpen) return;
        const target = event.target;
        if (target instanceof Node && megaMenu.contains(target)) return;
        resetState();
      },
      true,
    );

    megaMenu.addEventListener("focusout", (event) => {
      requestAnimationFrame(() => {
        if (isDebug()) return;
        const activeElement = document.activeElement;
        const relatedTarget = event.relatedTarget;

        if (activeElement instanceof Node && megaMenu.contains(activeElement)) return;
        if (relatedTarget instanceof Node && megaMenu.contains(relatedTarget)) return;

        resetState();
      });
    });

    window.addEventListener("resize", () => {
      const openPanelElement = panels.find((panel) => panel.dataset.open === "true") || null;
      const current = getCurrentTrigger();
      const activeHandle = openPanelElement?.dataset.panelHandle || current?.dataset.panelHandle || null;
      const activeTrigger = triggers.find((trigger) => trigger.dataset.panelHandle === activeHandle) || null;

      moveIndicator(activeTrigger);
    });

    resetState();
  }
};

const updateMegaMenuActiveStates = () => {
  const currentPath = window.location.pathname;
  const navParent =
    new URL(window.location.href).searchParams.get("nav_parent") ||
    document.querySelector("[data-nav-parent]")?.getAttribute("data-nav-parent") ||
    null;
  const links = document.querySelectorAll("[data-mega-menu-link]") as NodeListOf<HTMLAnchorElement>;

  // First pass: mark all matching links (skip links inside "Alle Produkte" groups)
  for (const link of links) {
    if (link.closest("[data-mega-menu-skip-active]")) {
      link.dataset.active = "false";
      continue;
    }

    const rawHref = link.getAttribute("href") || "";
    let hrefPath = rawHref.split("?")[0];
    try {
      hrefPath = new URL(rawHref, window.location.origin).pathname;
    } catch { /* keep as-is */ }

    const isActive = hrefPath === currentPath || (hrefPath.length > 1 && currentPath.startsWith(hrefPath));
    link.dataset.active = isActive.toString();
  }

  // Second pass: only keep active state in the first panel that has a match
  const panels = document.querySelectorAll("[data-mega-menu-panel]") as NodeListOf<HTMLElement>;
  let foundActivePanel = false;
  for (const panel of panels) {
    const hasActive = panel.querySelector('[data-mega-menu-link][data-active="true"]') !== null;
    if (hasActive && !foundActivePanel) {
      foundActivePanel = true;
    } else if (hasActive && foundActivePanel) {
      const dupeLinks = panel.querySelectorAll('[data-mega-menu-link][data-active="true"]') as NodeListOf<HTMLElement>;
      for (const link of dupeLinks) {
        link.dataset.active = "false";
      }
    }
  }

  // Propagate active state to top-level triggers
  // Priority: direct trigger href match > panel link match
  const triggers = document.querySelectorAll("[data-mega-menu-trigger][data-panel-handle]") as NodeListOf<HTMLElement>;

  // Filter to only triggers that actually have a non-empty panel handle
  const triggersWithPanels = Array.from(triggers).filter((t) => t.dataset.panelHandle);

  let directMatchTrigger: HTMLElement | null = null;

  if (navParent) {
    directMatchTrigger = triggersWithPanels.find((trigger) => trigger.dataset.panelHandle === navParent) || null;
  }

  if (!directMatchTrigger) {
    for (const trigger of triggersWithPanels) {
      const triggerHref = trigger.getAttribute("href") || "";
      let triggerPath = triggerHref;
      try { triggerPath = new URL(triggerHref, window.location.origin).pathname; } catch {}
      if (triggerPath.length > 1 && currentPath.startsWith(triggerPath)) {
        directMatchTrigger = trigger;
        break;
      }
    }
  }

  let foundActiveTrigger = false;
  for (const trigger of triggersWithPanels) {
    let isActive = false;
    if (directMatchTrigger) {
      isActive = trigger === directMatchTrigger;
    } else if (!foundActiveTrigger) {
      const handle = trigger.dataset.panelHandle;
      const panel = document.querySelector(`[data-mega-menu-panel][data-panel-handle="${handle}"]`);
      isActive = panel?.querySelector('[data-mega-menu-link][data-active="true"]') !== null;
    }
    trigger.dataset.current = isActive.toString();
    if (isActive) foundActiveTrigger = true;
  }

  // Propagate active state to submenu headlines
  // A headline is active if:
  //   1. It has active child links, OR
  //   2. Its own <a> href matches the current path (first match wins across all panels), OR
  //   3. Its panel trigger is a direct match and it's the first headline in that panel
  const groups = document.querySelectorAll("[data-mega-menu-group]") as NodeListOf<HTMLElement>;
  const usedDirectMatchPanels = new Set<string>();
  let foundHeadlineLinkMatch = false;

  for (const group of groups) {
    if (group.hasAttribute("data-mega-menu-skip-active")) continue;
    const headline = group.querySelector("[data-mega-menu-headline]") as HTMLElement | null;
    if (!headline) continue;

    const hasActiveLink = group.querySelector('[data-mega-menu-link][data-active="true"]') !== null;

    const panel = group.closest("[data-mega-menu-panel]") as HTMLElement | null;
    const panelHandle = panel?.dataset.panelHandle || "";
    const panelTrigger = panelHandle
      ? document.querySelector(`[data-mega-menu-trigger][data-panel-handle="${panelHandle}"]`) as HTMLElement | null
      : null;
    const triggerIsDirectMatch = panelTrigger === directMatchTrigger && directMatchTrigger !== null;

    // Check if the headline's own <a> matches — only use the first match across all panels
    const headlineLink = headline.closest("a");
    let headlineLinkMatches = false;
    if (headlineLink && !foundHeadlineLinkMatch) {
      let hlPath = headlineLink.getAttribute("href") || "";
      try { hlPath = new URL(hlPath, window.location.origin).pathname; } catch {}
      headlineLinkMatches = hlPath.length > 1 && currentPath.startsWith(hlPath);
    }

    const panelHasAnyActiveLink = panel?.querySelector('[data-mega-menu-link][data-active="true"]') !== null;
    const canUseDirectMatch = triggerIsDirectMatch && !panelHasAnyActiveLink && !usedDirectMatchPanels.has(panelHandle);
    const headlineActive = hasActiveLink || headlineLinkMatches || canUseDirectMatch;

    if (canUseDirectMatch && headlineActive) usedDirectMatchPanels.add(panelHandle);
    if (headlineLinkMatches) {
      foundHeadlineLinkMatch = true;
      // Also activate this headline's panel trigger
      if (panelTrigger && !foundActiveTrigger) {
        panelTrigger.dataset.current = "true";
      }
    }

    headline.dataset.active = headlineActive.toString();
  }
};

export { initEkMegaMenu, updateMegaMenuActiveStates };